import { useEffect, useState } from 'react';
import { toast } from 'App';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { formatDate } from 'utils/date';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { User } from 'views/types/typeUser';

interface IProps {
  user: User;
  onChanged: () => void;
}

const selectCls =
  'rounded-md border border-gray-200 bg-white px-2 py-1 text-sm dark:bg-navy-800 dark:text-white';

/**
 * Business client (KYB) administration for Invoice Payment eligibility:
 * client type, KYB status and the invoice_payment_enabled master switch.
 */
export const KybSection: React.FC<IProps> = ({ user, onChanged }) => {
  const { canUpdate } = usePermissions();
  const [clientType, setClientType] = useState(user.clientType ?? 'individual');
  const [kybStatus, setKybStatus] = useState(user.kybStatus ?? 'not_submitted');
  const [enabled, setEnabled] = useState(user.invoicePaymentEnabled ?? false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setClientType(user.clientType ?? 'individual');
    setKybStatus(user.kybStatus ?? 'not_submitted');
    setEnabled(user.invoicePaymentEnabled ?? false);
  }, [user]);

  const dirty =
    clientType !== (user.clientType ?? 'individual') ||
    kybStatus !== (user.kybStatus ?? 'not_submitted') ||
    enabled !== (user.invoicePaymentEnabled ?? false);

  const save = async () => {
    if (!canUpdate(PERMISSION_RESOURCES.INVOICE_KYB)) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to change KYB status',
        status: 'error',
      });
      return;
    }
    try {
      setSaving(true);
      await invoicePaymentsService.updateKyb(user.id, {
        clientType,
        kybStatus,
        invoicePaymentEnabled: enabled,
      });
      toast({ title: 'Business (KYB) status updated', status: 'success' });
      onChanged();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  // AMLBot's answer for the KYB applicant: docs verdict + the data the
  // verification form collected (company name, OTP-confirmed email/phone).
  const amlResult = user.kybVerifiedAt
    ? `✅ VALID (docs approved) — ${formatDate(
        user.kybVerifiedAt,
        'DD.MM.YYYY HH:mm'
      )}`
    : user.kybStatus === 'rejected'
      ? `❌ INVALID — ${user.kybDeclineReasons || 'no reasons given'}`
      : user.kybStatus === 'pending'
        ? '⏳ Awaiting AMLBot result'
        : '—';

  return (
    <ReadPermissionGate resource={PERMISSION_RESOURCES.INVOICE_KYB}>
      <li className="flex flex-col gap-2">
        <span>Business Client (Invoice Payment):</span>
        <div className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
          <span>
            Company:{' '}
            <span className="font-semibold">{user.kybCompanyName ?? '—'}</span>
          </span>
          <span>
            Verification contact: {user.kybEmail ?? '—'} ·{' '}
            {user.kybPhone ?? '—'}
          </span>
          <span>
            AMLBot result: <span className="font-semibold">{amlResult}</span>
            {user.kybApplicantId && (
              <a
                className="ml-2 text-brand-500 underline"
                href={`https://kyc-app.amlbot.com/applicants/${user.kybApplicantId}/overview`}
                target="_blank"
                rel="noreferrer"
              >
                Open in AMLBot
              </a>
            )}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-1">
            Type
            <select
              className={selectCls}
              value={clientType}
              onChange={e => setClientType(e.target.value)}
              disabled={!canUpdate(PERMISSION_RESOURCES.INVOICE_KYB)}
            >
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            KYB
            <select
              className={selectCls}
              value={kybStatus}
              onChange={e => setKybStatus(e.target.value)}
              disabled={!canUpdate(PERMISSION_RESOURCES.INVOICE_KYB)}
            >
              <option value="not_submitted">Not submitted</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              disabled={!canUpdate(PERMISSION_RESOURCES.INVOICE_KYB)}
            />
            Invoice Payment enabled
          </label>
          <button
            className="rounded bg-brand-500 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </li>
    </ReadPermissionGate>
  );
};
